import { useEffect, useRef, type PointerEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import styles from "./Sheet.module.css";

// RC1 · K1 (C27 1.14.9, E109): la hoja inferior del celular es una sola para todo el Admin. Fechas, «Más filtros», menús
// ⋮, secciones, áreas, cuenta, la cadena y el panel de detalle la usan; nadie dibuja la suya. Va al final del body (fuera
// de la fila de filtros que se desliza con su desvanecido) y lleva la clase `tm` para que sus tokens (--tm-surface,
// --tm-text…) existan también ahí: sin ella el fondo quedaba transparente y el calendario se leía sobre la página.

/** Distancia (px) que hay que arrastrar el tirador hacia abajo para cerrar la hoja. */
const dragToClose = 64;

/**
 * C27 · hoja inferior: superficie sólida (claro y oscuro), radio 18 px arriba, tirador 36 × 4 px, la página oscurecida
 * detrás y el área segura abajo. Se cierra tocando el fondo, arrastrando el tirador hacia abajo o con Esc (si no hay una
 * ventana encima); al abrir, el foco entra a la hoja y al cerrar vuelve a quien la abrió.
 */
export function Sheet({
  label,
  role = "dialog",
  sheetRef,
  onClose,
  children,
  full,
  className,
}: {
  label: string;
  role?: "dialog" | "menu";
  /** Clase de quien la usa para lo de adentro (alto de las opciones, botones); nunca la superficie ni el borde. */
  className?: string;
  /** Para quien necesita saber si un toque cayó dentro de la hoja (menús que se cierran al tocar afuera). */
  sheetRef?: RefObject<HTMLDivElement | null>;
  onClose: () => void;
  children: ReactNode;
  /** Contenido con su propio desplazamiento (panel de detalle): la hoja no se desplaza ni lleva relleno. */
  full?: boolean;
}) {
  const own = useRef<HTMLDivElement>(null);
  const panel = sheetRef ?? own;
  const panelRef = useRef(panel);
  const closeRef = useRef(onClose);
  const drag = useRef<{ y: number; id: number } | null>(null);
  useEffect(() => {
    closeRef.current = onClose;
    panelRef.current = panel;
  });
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current.current
      ?.querySelector<HTMLElement>("a[href], button:not(:disabled), input:not(:disabled)")
      ?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !document.querySelector("dialog[open]")) closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener?.isConnected && !panelRef.current.current?.contains(document.activeElement))
        opener.focus({ preventScroll: true });
    };
  }, []);

  const start = (event: PointerEvent<HTMLSpanElement>) => {
    drag.current = { y: event.clientY, id: event.pointerId };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  // La hoja es el padre del tirador: se mueve con el dedo y vuelve a su lugar si no llegó a cerrar.
  const move = (event: PointerEvent<HTMLSpanElement>) => {
    const sheet = event.currentTarget.parentElement;
    if (!drag.current || !sheet) return;
    const down = Math.max(0, event.clientY - drag.current.y);
    sheet.style.transform = down ? `translateY(${down}px)` : "";
  };
  const end = (event: PointerEvent<HTMLSpanElement>) => {
    const sheet = event.currentTarget.parentElement;
    const down = drag.current ? event.clientY - drag.current.y : 0;
    drag.current = null;
    if (sheet) sheet.style.transform = "";
    if (down >= dragToClose) onClose();
  };

  return createPortal(
    <div
      className={`tm ${styles.scrim}`}
      data-part="hoja-fondo"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        ref={panel}
        className={[styles.sheet, full ? styles.full : "", className ?? ""].filter(Boolean).join(" ")}
        role={role}
        aria-modal="true"
        aria-label={label}
        data-part="hoja"
      >
        <span
          className={styles.grab}
          aria-hidden="true"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
        >
          <span className={styles.handle} />
        </span>
        {children}
      </div>
    </div>,
    document.body,
  );
}
